import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import apiUrl from "../../Apiurl";

function EnvironmentVariablesPage({ utente }) {
  const { getAccessTokenSilently, user } = useAuth0();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const canDebug = Boolean(utente?.permessiAPI?.["read:debug"]);

  useEffect(() => {
    let ignore = false;

    const loadEnvironmentVariables = async () => {
      if (!canDebug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        const accessToken = await getAccessTokenSilently();
        const response = await fetch(
          `${apiUrl}/manutenzione/environment-variables`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (!ignore) {
          setSnapshot(data);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(`Variabili non caricate: ${loadError.message}`);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadEnvironmentVariables();

    return () => {
      ignore = true;
    };
  }, [canDebug, getAccessTokenSilently, user?.sub]);

  const normalizedSearch = search.trim().toLowerCase();

  const visibleVariables = useMemo(() => {
    const variables = snapshot?.visible || [];
    if (!normalizedSearch) {
      return variables;
    }

    return variables.filter((variable) => {
      const haystack = `${variable.name} ${variable.value}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [normalizedSearch, snapshot]);

  const redactedVariables = useMemo(() => {
    const variables = snapshot?.redacted || [];
    if (!normalizedSearch) {
      return variables;
    }

    return variables.filter((variable) => {
      const haystack = `${variable.name} ${variable.reason}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [normalizedSearch, snapshot]);

  if (!canDebug) {
    return (
      <div className="pa2 pa3-ns">
        <div className="sfondoBiancoTema br3 pa3">
          <h3 className="mt0 mb2">Variabili ambiente</h3>
          <p className="mb0 dark-red">
            Non hai i permessi per visualizzare questa pagina.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pa2 pa3-ns">
      <div className="mw9 center sfondoBiancoTema br3 pa3 pa4-l">
        <div className="flex flex-wrap justify-between items-start mb3">
          <div className="pr3">
            <h1 className="mt0 mb2">Variabili ambiente</h1>
            <p className="mt0 mb2 dark-gray">
              Sono visibili solo le variabili il cui nome non contiene pattern
              riconducibili a password, token, secret, chiavi o credenziali.
            </p>
            {snapshot?.generatedAt && (
              <p className="mt0 mb0 dark-gray">
                Rilevate: {new Date(snapshot.generatedAt).toLocaleString()}
              </p>
            )}
          </div>

          {snapshot && (
            <div className="flex flex-wrap gap-2">
              <div className="ba b--black-20 br2 pa2 mr2 mb2">
                <div className="f6 dark-gray">Totali</div>
                <div className="f4 b">{snapshot.total}</div>
              </div>
              <div className="ba b--black-20 br2 pa2 mr2 mb2">
                <div className="f6 dark-gray">Visibili</div>
                <div className="f4 b dark-green">{snapshot.visibleCount}</div>
              </div>
              <div className="ba b--black-20 br2 pa2 mb2">
                <div className="f6 dark-gray">Oscurate</div>
                <div className="f4 b dark-red">{snapshot.redactedCount}</div>
              </div>
            </div>
          )}
        </div>

        <label className="db mb3">
          <span className="db b mb2">Filtro</span>
          <input
            className="w-100 pa2 ba b--black-20 br2"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Cerca per nome o valore"
          />
        </label>

        {loading && <div className="pa3 ba b--black-20 br2">Caricamento...</div>}

        {error && <div className="pa3 ba b--dark-red dark-red br2">{error}</div>}

        {!loading && !error && snapshot && (
          <>
            <h2 className="mt4 mb2">Valori visibili</h2>
            <div className="overflow-auto ba b--black-10 br2 mb4">
              <table className="w-100 collapse">
                <thead>
                  <tr className="tl bg-near-white">
                    <th className="pa2 bb b--black-10">Nome</th>
                    <th className="pa2 bb b--black-10">Valore</th>
                    <th className="pa2 bb b--black-10 tr">Lunghezza</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleVariables.map((variable) => (
                    <tr key={variable.name} className="striped--near-white">
                      <td className="pa2 bb b--black-10 v-top code">
                        {variable.name}
                      </td>
                      <td className="pa2 bb b--black-10 v-top code ws-normal">
                        {variable.value}
                      </td>
                      <td className="pa2 bb b--black-10 v-top tr">
                        {variable.length}
                      </td>
                    </tr>
                  ))}
                  {visibleVariables.length === 0 && (
                    <tr>
                      <td className="pa3 dark-gray" colSpan="3">
                        Nessuna variabile visibile per il filtro impostato.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <h2 className="mt4 mb2">Variabili oscurate</h2>
            <div className="overflow-auto ba b--black-10 br2">
              <table className="w-100 collapse">
                <thead>
                  <tr className="tl bg-near-white">
                    <th className="pa2 bb b--black-10">Nome</th>
                    <th className="pa2 bb b--black-10">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {redactedVariables.map((variable) => (
                    <tr key={variable.name} className="striped--near-white">
                      <td className="pa2 bb b--black-10 v-top code">
                        {variable.name}
                      </td>
                      <td className="pa2 bb b--black-10 v-top">
                        {variable.reason}
                      </td>
                    </tr>
                  ))}
                  {redactedVariables.length === 0 && (
                    <tr>
                      <td className="pa3 dark-gray" colSpan="2">
                        Nessuna variabile oscurata per il filtro impostato.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <h2 className="mt4 mb2">Oggetto autenticazione</h2>
            <div className="ba b--black-10 br2 mb4 overflow-auto">
              <pre className="ma0 pa3 code ws-pre">
                {JSON.stringify(utente, null, 2)}
              </pre>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default EnvironmentVariablesPage;
